import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Button variants with clear intent naming
const buttonVariants = cva(
  "aero:inline-flex aero:items-center aero:justify-center aero:gap-2 aero:whitespace-nowrap aero:font-medium aero:select-none aero:appearance-none aero:transition-[background,border,color,box-shadow,transform] aero:outline-none aero:cursor-pointer aero:disabled:cursor-not-allowed aero:[&_svg]:pointer-events-none aero:[&_svg:not([class*='size-'])]:size-4 aero:active:scale-[0.98] aero:disabled:opacity-50 aero:disabled:pointer-events-none aero:focus-visible:ring-[3px] aero:focus-visible:ring-ring/50",
  {
    variants: {
      intent: {
        primary: "",
        secondary: "",
        destructive: "", // Red/Orange - for delete, remove, warning actions
        tertiary: "", // Alias for destructive (backward compatibility)
        neutral: "", // Gray/Neutral - for cancel, close, dismiss actions
        custom: "", // For custom styling via className
      },
      design: {
        solid: "",
        outline: "",
        text: "",
      },
      shape: {
        rectangle: "aero:rounded",
        rounded: "aero:rounded-lg",
        oval: "aero:rounded-full",
      },
      size: {
        sm: "aero:h-9 aero:px-3 aero:py-2 aero:text-sm aero:gap-1.5",
        md: "aero:h-10 aero:px-4 aero:py-2.5 aero:text-sm aero:gap-2",
        lg: "aero:h-12 aero:px-6 aero:py-3 aero:text-base aero:gap-2",
        iconSm: "aero:size-9 aero:p-0",
        iconMd: "aero:size-10 aero:p-0",
        iconLg: "aero:size-12 aero:p-0",
      },
    },
    compoundVariants: [
      // PRIMARY (Teal/Dark Cyan - Main brand color from Figma)
      {
        intent: "primary",
        design: "solid",
        class:
          "aero:bg-brand-primary-700 aero:text-white aero:border-transparent aero:hover:bg-brand-primary-800 aero:active:bg-brand-primary-900 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-brand-primary-700/60",
      },
      {
        intent: "primary",
        design: "outline",
        class:
          "aero:bg-white aero:text-brand-primary-700 aero:border aero:border-brand-primary-700 aero:hover:bg-brand-primary-50 aero:active:bg-brand-primary-100 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-brand-primary-700/60",
      },
      {
        intent: "primary",
        design: "text",
        class:
          "aero:bg-transparent aero:text-brand-primary-700 aero:hover:bg-brand-primary-50 aero:active:bg-brand-primary-100 aero:border-transparent aero:disabled:text-neutral-500 aero:focus-visible:ring-brand-primary-700/60",
      },

      // SECONDARY (Blue from Figma design system)
      {
        intent: "secondary",
        design: "solid",
        class:
          "aero:bg-blue-700 aero:text-white aero:border-transparent aero:hover:bg-blue-800 aero:active:bg-blue-900 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-blue-700/60",
      },
      {
        intent: "secondary",
        design: "outline",
        class:
          "aero:bg-white aero:text-blue-700 aero:border aero:border-blue-700 aero:hover:bg-blue-50 aero:active:bg-blue-100 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-blue-700/60",
      },
      {
        intent: "secondary",
        design: "text",
        class:
          "aero:bg-transparent aero:text-blue-700 aero:hover:bg-blue-50 aero:active:bg-blue-100 aero:border-transparent aero:disabled:text-neutral-500 aero:focus-visible:ring-blue-700/60",
      },

      // DESTRUCTIVE (Red/Orange - for delete, remove, cancel, warning actions)
      {
        intent: "destructive",
        design: "solid",
        class:
          "aero:bg-secondary-600 aero:text-white aero:border-transparent aero:hover:bg-secondary-700 aero:active:bg-secondary-800 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-secondary-600/60",
      },
      {
        intent: "destructive",
        design: "outline",
        class:
          "aero:bg-white aero:text-secondary-600 aero:border aero:border-secondary-600 aero:hover:bg-secondary-50 aero:active:bg-secondary-100 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-secondary-600/60",
      },
      {
        intent: "destructive",
        design: "text",
        class:
          "aero:bg-transparent aero:text-secondary-600 aero:hover:bg-secondary-50 aero:active:bg-secondary-100 aero:border-transparent aero:disabled:text-neutral-500 aero:focus-visible:ring-secondary-600/60",
      },

      // TERTIARY (Alias for destructive - backward compatibility)
      {
        intent: "tertiary",
        design: "solid",
        class:
          "aero:bg-secondary-600 aero:text-white aero:border-transparent aero:hover:bg-secondary-700 aero:active:bg-secondary-800 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-secondary-600/60",
      },
      {
        intent: "tertiary",
        design: "outline",
        class:
          "aero:bg-white aero:text-secondary-600 aero:border aero:border-secondary-600 aero:hover:bg-secondary-50 aero:active:bg-secondary-100 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-secondary-600/60",
      },
      {
        intent: "tertiary",
        design: "text",
        class:
          "aero:bg-transparent aero:text-secondary-600 aero:hover:bg-secondary-50 aero:active:bg-secondary-100 aero:border-transparent aero:disabled:text-neutral-500 aero:focus-visible:ring-secondary-600/60",
      },

      // NEUTRAL (Gray - for cancel, close, dismiss, back actions)
      {
        intent: "neutral",
        design: "solid",
        class:
          "aero:bg-neutral-500 aero:text-white aero:border-transparent aero:hover:bg-neutral-600 aero:active:bg-neutral-700 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-neutral-500/60",
      },
      {
        intent: "neutral",
        design: "outline",
        class:
          "aero:bg-white aero:text-neutral-500 aero:border aero:border-neutral-500 aero:hover:bg-neutral-50 aero:active:bg-neutral-100 aero:disabled:bg-neutral-300 aero:disabled:text-neutral-500 aero:disabled:border-neutral-300 aero:focus-visible:ring-neutral-500/60",
      },
      {
        intent: "neutral",
        design: "text",
        class:
          "aero:bg-transparent aero:text-neutral-500 aero:hover:bg-neutral-50 aero:active:bg-neutral-100 aero:border-transparent aero:disabled:text-neutral-400 aero:focus-visible:ring-neutral-500/60",
      },

      // CUSTOM (No preset styles - fully customizable via className)
      {
        intent: "custom",
        design: "solid",
        class: "",
      },
      {
        intent: "custom",
        design: "outline",
        class: "aero:border",
      },
      {
        intent: "custom",
        design: "text",
        class: "aero:bg-transparent aero:border-transparent",
      },
    ],
    defaultVariants: {
      intent: "primary",
      design: "solid",
      shape: "rectangle",
      size: "md",
    },
  }
);

type ButtonProps = React.ComponentProps<"button"> & {
  asChild?: boolean;
  size?: VariantProps<typeof buttonVariants>["size"];
  intent?:
    | "primary"
    | "secondary"
    | "destructive"
    | "tertiary"
    | "neutral"
    | "custom";
  design?: "solid" | "outline" | "text";
  shape?: "rectangle" | "rounded" | "oval";
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, size, asChild = false, intent, design, shape, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(
          buttonVariants({ intent, design, shape, size }),
          className
        )}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
